import React, { useState, useEffect } from 'react';
import {
    Box,
    Stack,
    Typography,
    useMediaQuery,
    Paper,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InputField from '../components/InputField/InputField';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ActionButton, CancelButton } from '../components/Button';
import { userService } from '../services/userService';
import { fileService } from '../services/fileService';
import { useResponsiveToast } from '../components/Toast/Toast';
import ChangePasswordModal from '../components/ChangePasswordModal';

interface FormData {
    username: string;
    email: string;
    profile_picture: File | null;
}

const MyAccountPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { success: showSuccess, error: showError } = useResponsiveToast();

    // Form state
    const [formData, setFormData] = useState<FormData>({
        username: '',
        email: '',
        profile_picture: null,
    });

    // Modal state
    const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);

    // UI state
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [existingProfilePicture, setExistingProfilePicture] = useState<string | null>(null);
    const [fetchingUser, setFetchingUser] = useState(false);
    const [fetchingProfilePicture, setFetchingProfilePicture] = useState(false);

    // Load user data
    useEffect(() => {
        loadUserData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadUserData = async () => {
        try {
            setFetchingUser(true);
            const user = await userService.getMyAccount();
            
            setFormData({
                username: user.username,
                email: user.email,
                profile_picture: null,
            });


            // Unblock the page immediately after setting form data
            setFetchingUser(false);

            // Load existing profile picture in the background if available
            if (user.profile_picture) {
                setFetchingProfilePicture(true);
                try {
                    const fileResponse = await fileService.previewFile(user.profile_picture);
                    const blob = fileResponse.data;
                    const imageUrl = URL.createObjectURL(blob);
                    setExistingProfilePicture(imageUrl);
                    setImagePreview(imageUrl);
                } catch (error) {
                    console.error('Error loading profile picture:', error);
                    // Continue without profile picture if it fails to load
                } finally {
                    setFetchingProfilePicture(false);
                }
            }
        } catch (error) {
            console.error('Full error object:', error);
            showError('Failed to load account data');
            console.error('Error loading account:', error);
            setFetchingUser(false);
        }
    };


    // Validate file type
    const isValidImageType = (file: File): boolean => {
        const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
        return allowedTypes.includes(file.type.toLowerCase());
    };

    // Handle file upload
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        
        if (file) {
            // Validate file type
            if (!isValidImageType(file)) {
                showError('Please select a valid image file (PNG, JPG, or JPEG)');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                showError('File size must be less than 5MB');
                return;
            }

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            
            setFormData(prev => ({
                ...prev,
                profile_picture: file
            }));

            // Automatically upload the new profile picture
            try {
                const currentUser = await userService.getMyAccount();
                await userService.uploadProfilePicture(currentUser.id, file);
                showSuccess('Profile picture updated successfully!');
            } catch (err) {
                showError(err instanceof Error ? err.message : 'Failed to update profile picture');
                console.error('Error updating profile picture:', err);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                profile_picture: null
            }));
            setImagePreview(null);
        }
    };

    // Handle profile picture removal
    const handleRemoveProfilePicture = () => {
        setFormData(prev => ({ ...prev, profile_picture: null }));
        setImagePreview(null);
        setExistingProfilePicture(null);
        
        // Clean up existing preview URL
        if (existingProfilePicture) {
            URL.revokeObjectURL(existingProfilePicture);
        }
    };

    // Clean up preview URL when component unmounts or image changes
    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview !== existingProfilePicture) {
                URL.revokeObjectURL(imagePreview);
            }
            if (existingProfilePicture) {
                URL.revokeObjectURL(existingProfilePicture);
            }
        };
    }, [imagePreview, existingProfilePicture]);


    if (fetchingUser) {
        return (
            <Box sx={{ 
                background: '#FEFBFF', 
                minHeight: '100vh', 
                p: { xs: 1, sm: 4 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pt: { xs: 8, sm: 10 }, // Account for TopBar height
                px: { xs: 1, sm: 4 }, // Add horizontal padding for mobile
                overflow: 'hidden', // Prevent horizontal overflow
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            background: '#FEFBFF', 
            minHeight: '100vh', 
            p: { xs: 1, sm: 4 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: { xs: 8, sm: 10 }, // Account for TopBar height (64px + padding)
            px: { xs: 1, sm: 4 }, // Add horizontal padding for mobile
            overflow: 'hidden', // Prevent horizontal overflow
        }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: "16px", sm: "28px" },
                    backgroundColor: '#fff',
                    p: { xs: 2, sm: 3 },
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '600px', md: '700px' },
                    mx: 'auto',
                    overflow: 'hidden', // Prevent content overflow
                }}
            >
                {/* Header */}
                <Typography
                    fontFamily='Poppins, sans-serif'
                    fontSize={{ xs: 20, sm: 24 }}
                    fontWeight={600}
                    color='#132F5F'
                    mb={{ xs: 2, sm: 3 }}
                    textAlign={{ xs: 'center', sm: 'left' }}
                >
                    My Account
                </Typography>

                {/* Form Section */}
                <Stack spacing={{ xs: 2, sm: 3 }}>
                    <InputField
                        label='Username'
                        placeholder='Enter username'
                        required
                        value={formData.username}
                        disabled
                        sx={{
                            input: { 
                                fontWeight: 500,
                                backgroundColor: '#F5F5F5',
                                color: '#666'
                            },
                            width: '100%',
                            height: '56px',
                        }}
                    />

                    <InputField
                        label='Email address'
                        type='email'
                        placeholder='Enter email address'
                        required
                        value={formData.email}
                        disabled
                        sx={{
                            input: {
                                backgroundColor: '#F5F5F5',
                                color: '#666'
                            },
                            width: '100%',
                            height: '56px',
                        }}
                    />

                    {/* Change Password Button */}
                    <Box>
                        <Typography
                            onClick={() => setChangePasswordModalOpen(true)}
                            sx={{
                                color: '#132F5F',
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                fontSize: 14,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#001742',
                                },
                            }}
                        >
                            Change Password
                        </Typography>
                    </Box>


                    {/* File Upload Section */}
                    <Box
                        sx={{
                            border: '2px dashed #132F5F',
                            borderRadius: { xs: '8px', sm: '16px' },
                            padding: { xs: 1.5, sm: 3 },
                            textAlign: 'center',
                            backgroundColor: '#FEFBFF',
                            width: '100%',
                            maxWidth: { xs: '100%', sm: '400px' },
                            mx: 'auto',
                            position: 'relative',
                            minHeight: imagePreview ? (isMobile ? '120px' : '200px') : 'auto',
                            overflow: 'hidden', // Prevent content overflow
                            boxSizing: 'border-box', // Ensure padding is included in width
                        }}
                    >
                        {fetchingProfilePicture ? (
                            // Show loader while fetching profile picture
                            <Stack spacing={2} alignItems='center' justifyContent='center' sx={{ minHeight: { xs: '150px', sm: '200px' } }}>
                                <CircularProgress size={isMobile ? 30 : 40} sx={{ color: '#132F5F' }} />
                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontSize={{ xs: 12, sm: 14 }}
                                    color='#132F5F'
                                    textAlign='center'
                                >
                                    Loading profile picture...
                                </Typography>
                            </Stack>
                        ) : imagePreview ? (
                            // Show image preview
                            <Box sx={{ 
                                position: 'relative', 
                                width: '100%', 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                overflow: 'hidden',
                                maxHeight: isMobile ? '150px' : '300px'
                            }}>
                                <img
                                    src={imagePreview}
                                    alt="Profile preview"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: isMobile ? '150px' : '300px',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                    }}
                                />
                                <CancelButton
                                    onClick={handleRemoveProfilePicture}
                                    sx={{
                                        position: 'absolute',
                                        top: { xs: 2, sm: 8 },
                                        right: { xs: 2, sm: 8 },
                                        minWidth: 'auto',
                                        width: { xs: 24, sm: 32 },
                                        height: { xs: 24, sm: 32 },
                                        borderRadius: '50%',
                                        backgroundColor: '#E53935',
                                        color: 'white',
                                        border: 'none',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        fontSize: { xs: '10px', sm: '14px' },
                                        '&:hover': {
                                            backgroundColor: '#C62828',
                                            border: 'none',
                                        },
                                    }}
                                >
                                    ✕
                                </CancelButton>
                            </Box>
                        ) : (
                            // Show upload interface when no image is selected
                            <Stack spacing={{ xs: 1, sm: 2 }} alignItems='center' sx={{ width: '100%' }}>
                                <CloudUploadIcon sx={{ fontSize: { xs: 24, sm: 40 }, color: '#132F5F' }} />
                                
                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontWeight={500}
                                    fontSize={{ xs: 12, sm: 16 }}
                                    color='#132F5F'
                                    textAlign='center'
                                    sx={{ lineHeight: 1.2 }}
                                >
                                    Upload Profile Picture
                                </Typography>
                                
                                {formData.profile_picture && (
                                    <Typography
                                        fontFamily='Poppins, sans-serif'
                                        fontSize={{ xs: 9, sm: 12 }}
                                        color='#666'
                                        textAlign='center'
                                        sx={{ 
                                            wordBreak: 'break-word',
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Selected: {formData.profile_picture.name}
                                    </Typography>
                                )}

                                {/* Hidden file input */}
                                <ActionButton
                                    component="label"
                                    sx={{
                                        px: { xs: 1.5, sm: 3 },
                                        py: { xs: 0.75, sm: 1.5 },
                                        backgroundColor: '#132F5F',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        fontSize: { xs: 11, sm: 14 },
                                        minWidth: { xs: 'auto', sm: 'auto' },
                                        width: { xs: '100%', sm: 'auto' },
                                        maxWidth: { xs: '200px', sm: 'none' },
                                        '&:hover': {
                                            backgroundColor: '#132F5F',
                                        },
                                    }}
                                >
                                    Choose from device
                                    <input 
                                        hidden 
                                        accept="image/png,image/jpg,image/jpeg" 
                                        type="file" 
                                        onChange={handleFileChange}
                                    />
                                </ActionButton>

                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontSize={{ xs: 8, sm: 10 }}
                                    color='#666'
                                    textAlign='center'
                                    sx={{ 
                                        mt: 0.5,
                                        lineHeight: 1.2,
                                        maxWidth: '100%',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    Accepted formats: PNG, JPG, JPEG (Max 5MB)
                                </Typography>
                            </Stack>
                        )}
                    </Box>

                </Stack>

                {/* Back Button */}
                <Box sx={{ mt: { xs: 3, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
                    <CancelButton
                        onClick={() => window.history.back()}
                        sx={{
                            borderColor: '#132F5F',
                            color: '#132F5F',
                            borderRadius: { xs: '8px', sm: '12px' },
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1, sm: 1.5 },
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            fontSize: { xs: 12, sm: 14 },
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: 'auto', sm: '180px' },
                            '&:hover': {
                                borderColor: '#132F5F',
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        ← Back
                    </CancelButton>
                </Box>
            </Paper>

            {/* Change Password Modal */}
            <ChangePasswordModal
                open={changePasswordModalOpen}
                onClose={() => setChangePasswordModalOpen(false)}
            />
        </Box>
    );
};

export default MyAccountPage;
